import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { isEmpty, map, noop } from "lodash";
import { push } from "store/actions/router";
import { FiChevronDown } from "react-icons/fi";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Col, Row } from "react-bootstrap";
import { isBankVerifiedSelector } from "store/selectors/bank";
import type { RootState } from "types/state";

import Button from "components/Button";
import classnames from "classnames";
import * as Collapsible from "@radix-ui/react-collapsible";
import Step from "./Step";

import "./Accordian.scss";

interface AccordianStep {
  index: number;
  label?: string;
  subtext?: string;
  isAsync?: boolean;
  content?: React.ReactNode;
  actions?: React.ReactNode[];
}

interface OwnProps {
  onClick?: (...args: unknown[]) => void;
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  actionText?: string;
  completedAction?: boolean;
  loading?: boolean;
  disabled?: boolean;
  img?: (...args: unknown[]) => void;
  isFetching?: boolean;
  isActive?: boolean;
  color?: string;
  activeStepNumber?: number;
  steps?: AccordianStep[];
  btnIcon?: React.ReactNode;
  btnIconPosition?: string;
}

const mapStateToProps = (state: RootState) => {
  return {
    userHasGroup: !isEmpty(state.employer.groups),
    hasGroup: !isEmpty(state.employer.groups),
    hasBank: isBankVerifiedSelector(state),
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps> & PropsFromRedux;

class Accordian extends React.PureComponent<Props> {
  // Original constructor called super() with no args; super(props) is the
  // idiomatic TS form and is behavior-equivalent (React sets props regardless).
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  verificationCheckMark = () => {
    return (
      <svg
        className="checkmark"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 52 52"
      >
        <circle
          className="checkmark__circle"
          cx="26"
          cy="26"
          r="25"
          fill="none"
        />
        <path
          className="checkmark__check"
          fill="none"
          d="M14.1 27.2l7.1 7.2 16.7-16.8"
        />
      </svg>
    );
  };

  _accordianActionBtn = () => {
    const disabled = this.props.disabled;
    const color = disabled ? "#858c9c" : "#0a2540";

    const doesNotHaveSteps = isEmpty(this.props.steps);

    if (!doesNotHaveSteps && !this.props.hasGroup) {
      return (
        <div
          className={classnames("chevron", {
            disabled,
          })}
        >
          <FiChevronDown color="white" stroke={color} size={16} />
        </div>
      );
    } else if (
      this.props.hasBank &&
      this.props.hasGroup &&
      this.props.completedAction
    ) {
      return (
        <div
          className={classnames("chevron", {
            disabled,
          })}
        >
          <FiChevronDown color="white" stroke={color} size={16} />
        </div>
      );
    } else if (isEmpty(this.props.steps)) {
      return (
        <div
          className={classnames("accordian-button", {
            disabled,
          })}
        >
          <Button
            size="sm"
            name="action"
            // color/position are looser `string` props here than Button's
            // unions; cast preserves the original runtime value passthrough.
            color={
              this.props.color as React.ComponentProps<typeof Button>["color"]
            }
            icon={{
              icon: this.props.btnIcon,
              position: this.props.btnIconPosition as "left" | "right",
            }}
            onClick={disabled ? noop : this.props.onClick}
            loading={this.props.isFetching}
          >
            {this.props.actionText}
          </Button>
        </div>
      );
    }
  };

  _accordianItem = () => {
    const { disabled, completedAction } = this.props;

    return (
      <div
        className={classnames("accordian-item", {
          disabled,
          completedAction,
        })}
      >
        <div className="item">
          <div className="circle">
            {this.props.icon && <span className="icon">{this.props.icon}</span>}
          </div>

          <div className="item-description">
            <p className="title">{this.props.title}</p>
            {this.props.description && (
              <p className="description">{this.props.description}</p>
            )}
          </div>
        </div>

        {this.props.completedAction && (
          <div className="completed-action">
            <div>{this.verificationCheckMark()}</div>
          </div>
        )}
        {!this.props.completedAction && <div>{this._accordianActionBtn()}</div>}
      </div>
    );
  };

  render() {
    const { disabled, completedAction } = this.props;
    const isOpen = !disabled && !completedAction;
    const doesNotHaveSteps = isEmpty(this.props.steps);

    return (
      <>
        <Collapsible.Root
          open={isOpen}
          disabled={disabled || completedAction || isOpen}
          onOpenChange={() => {}}
        >
          <Collapsible.Trigger asChild>
            {this._accordianItem()}
          </Collapsible.Trigger>
          <Collapsible.Content>
            {!doesNotHaveSteps && (
              <div className="accordian-body">
                <Row>
                  <Col className="step-col">
                    {map(this.props.steps, (step, idx) => {
                      return (
                        <div className="active-step">
                          <Step
                            isActive={this.props.activeStepNumber == step.index}
                            key={step.index}
                            label={step.label}
                            subtext={step.subtext}
                            index={idx + 1}
                            isAsync={step.isAsync}
                            completedStep={
                              this.props.activeStepNumber! > step.index
                            }
                            content={step.content}
                          />

                          {this.props.activeStepNumber == step.index &&
                            this.props.onClick && (
                              <div className="step-btn">
                                <Button
                                  size="sm"
                                  name="action"
                                  onClick={disabled ? noop : this.props.onClick}
                                  loading={this.props.isFetching}
                                >
                                  {this.props.actionText}
                                </Button>
                              </div>
                            )}
                          {this.props.activeStepNumber == step.index &&
                            step.actions && (
                              <>
                                <div
                                  style={{
                                    flexBasis: "100%",
                                    height: 0,
                                  }}
                                ></div>
                                <div className="step-actions">
                                  {map(step.actions, (action) => action)}
                                </div>
                              </>
                            )}
                        </div>
                      );
                    })}
                  </Col>
                </Row>
              </div>
            )}
          </Collapsible.Content>
        </Collapsible.Root>
      </>
    );
  }
}

export default connector(withApollo<Props>(Accordian));
